/**
 * V2, the page is the world (docs/ideas/loom-v2.md, phase 1). The scene's weather, light and crowd (the canon pass's read)
 * go on the root as data-weather / data-light / data-crowd, the newest reply's prose is marked data-now, and each reply's
 * prose keeps the weather and crowd it was written in (data-w, data-c), so every theme's CSS can take them on (world.css
 * has the shared rules). Rain, storm and snow also fall over the page; a new place
 * tears the woven band at the top and weaves it again.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Sky } from "../../shared/story";
import type { StoryUI } from "../useStory";
import "./world.css";

const reduced = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Puts the scene's sky on the root and marks the newest prose; returns the sky and a counter that ticks on a new place. */
export function useWorld(s: StoryUI): { sky: Sky | null; moved: number } {
  const sky = s.id ? s.view?.sky ?? null : null;
  const root = document.documentElement;
  useEffect(() => {
    if (sky) { root.dataset.weather = sky.weather; root.dataset.light = sky.light; root.dataset.crowd = String(sky.crowd); }
    else { delete root.dataset.weather; delete root.dataset.light; delete root.dataset.crowd; }
  }, [sky?.weather, sky?.light, sky?.crowd]);
  useEffect(() => () => { delete root.dataset.weather; delete root.dataset.light; delete root.dataset.crowd; }, []);

  // The newest reply (the one being written, else the last) is "now"; everything before it is behind you. Once the
  // player sends, the last reply is already behind them: its now-only styles go before the send's one scroll measures
  // the page (steady reading, common.tsx ReadingRoom), not under the reader's eyes when the reply starts.
  const now = s.live?.n ?? (s.pending ? null : s.view?.turns.at(-1)?.n ?? null);
  const shown = `${now}|${s.turns.length}|${s.live?.text.length ? 1 : 0}|${s.view?.n ?? ""}|${s.pending ?? ""}`;
  // The weather and crowd a reply was written in stay with it (data-w, data-c): what changes its layout (slant,
  // spacing, leading) never changes on text already on the page when the canon pass reads a new sky, only on the next
  // reply. Kept by turn, so a reply that settles into a new element keeps its own.
  const stamps = useRef(new Map<string, { w: string; c: string }>());
  useLayoutEffect(() => {
    for (const el of document.querySelectorAll<HTMLElement>("[data-prose]")) {
      const n = el.dataset.prose ?? "";
      if (now !== null && n === String(now)) el.dataset.now = "";
      else delete el.dataset.now;
      const key = `${s.id}:${n}`;
      let st = stamps.current.get(key);
      if (!st) {
        const own = s.view?.turns.find((t) => String(t.n) === n)?.sky ?? sky;
        st = { w: own?.weather ?? "none", c: own ? String(own.crowd) : "none" };
        stamps.current.set(key, st);
      }
      if (el.dataset.w !== st.w) el.dataset.w = st.w;
      if (el.dataset.c !== st.c) el.dataset.c = st.c;
    }
  }, [shown]);

  // A new place (not the first one read, not a story switch) tears the band.
  const [moved, setMoved] = useState(0);
  const last = useRef<{ id: string | null; here: string | null }>({ id: null, here: null });
  const here = s.view?.here ?? null;
  useEffect(() => {
    const was = last.current;
    if (s.id && was.id === s.id && was.here && here && here !== was.here) setMoved((m) => m + 1);
    last.current = { id: s.id, here };
  }, [s.id, here]);
  return { sky, moved };
}

/** Rain, storm or snow over the page (nothing for other weather, or with reduced motion). */
export function SkyLayer({ sky }: { sky: Sky | null }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const weather = sky?.weather;
  const falls = weather === "rain" || weather === "storm" || weather === "snow";
  useEffect(() => {
    const c = ref.current;
    if (!c || !falls || reduced()) return;
    const x = c.getContext("2d");
    if (!x) return;
    let w = 0, h = 0, raf = 0, stopped = false;
    const size = () => { w = c.width = innerWidth; h = c.height = innerHeight; };
    size();
    const snow = weather === "snow", storm = weather === "storm";
    const count = snow ? 70 : storm ? 160 : 100;
    const drops = Array.from({ length: count }, () => ({ x: Math.random() * w * 1.2, y: Math.random() * h, l: 8 + Math.random() * 14, v: snow ? 0.4 + Math.random() * 0.8 : 5 + Math.random() * 6, r: 1 + Math.random() * 1.6, p: Math.random() * 6.28 }));
    const ink = getComputedStyle(document.body).color || "#ccc";
    let flash = 0;
    const tick = () => {
      if (stopped) return;
      x.clearRect(0, 0, w, h);
      x.globalAlpha = snow ? 0.35 : storm ? 0.26 : 0.18;
      x.strokeStyle = ink; x.fillStyle = ink; x.lineWidth = 1;
      if (snow) {
        for (const d of drops) {
          d.p += 0.01; d.y += d.v; d.x += Math.sin(d.p) * 0.4;
          if (d.y > h) { d.y = -6; d.x = Math.random() * w; }
          x.beginPath(); x.arc(d.x, d.y, d.r, 0, 6.283); x.fill();
        }
      } else {
        const slant = storm ? 0.45 : 0.25;
        x.beginPath();
        for (const d of drops) {
          x.moveTo(d.x, d.y); x.lineTo(d.x - d.l * slant, d.y + d.l);
          d.y += d.v * (storm ? 1.4 : 1); d.x -= d.v * slant;
          if (d.y > h) { d.y = -20; d.x = Math.random() * w * 1.2; }
        }
        x.stroke();
        // A storm flashes now and then: the page lights for a moment.
        if (storm && flash <= 0 && Math.random() < 0.002) flash = 10;
        if (flash > 0) { x.globalAlpha = flash / 90; x.fillRect(0, 0, w, h); flash--; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    addEventListener("resize", size);
    return () => { stopped = true; cancelAnimationFrame(raf); removeEventListener("resize", size); x.clearRect(0, 0, w, h); };
  }, [weather, falls]);
  return falls ? <canvas ref={ref} className="v2-sky" aria-hidden="true" /> : null;
}

/** The band at the top of the page, torn and woven again when the player reaches a new place. */
export function Tear({ moved }: { moved: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!moved || !c || reduced()) return;
    const x = c.getContext("2d");
    if (!x) return;
    const w = c.clientWidth, dpr = devicePixelRatio || 1, H = 14;
    c.width = w * dpr; c.height = H * dpr;
    x.setTransform(dpr, 0, 0, dpr, 0, 0);
    const ink = getComputedStyle(document.body).color || "#ccc";
    const seed = () => Array.from({ length: Math.ceil(w / 3) }, (_, i) => ({ x: i * 3 + 1, h: 4 + Math.random() * 10, a: 0.25 + Math.random() * 0.6, f: Math.random() }));
    let threads = seed(), reseeded = false, raf = 0;
    const t0 = performance.now(), dur = 1700;
    const draw = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      if (p > 0.5 && !reseeded) { threads = seed(); reseeded = true; }
      const gap = Math.sin(Math.PI * p) * w * 0.26, cx = w / 2;
      x.clearRect(0, 0, w, H);
      x.fillStyle = ink;
      for (const t of threads) {
        const d = Math.abs(t.x - cx) - gap / 2;
        if (d < 0) continue;
        const h = gap > 0 && d < 22 ? t.h * (0.15 + (d / 22) * (0.4 + t.f * 0.6)) : t.h;
        x.globalAlpha = t.a * (1 - Math.abs(0.5 - p) * 0.6);
        x.fillRect(t.x, (H - h) / 2, 1, h);
      }
      if (p < 1) raf = requestAnimationFrame(draw);
      else x.clearRect(0, 0, w, H);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [moved]);
  return <canvas ref={ref} className="v2-tear" aria-hidden="true" />;
}
